/**
 * Values Section JavaScript
 * File: assets/values-section.js
 * Purpose: Interactive effects and animations for values section
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  document.addEventListener('DOMContentLoaded', initValuesSection);

  function initValuesSection() {
    // Initialize all components
    initScrollAnimations();
    initParallaxEffect();
    initHoverEffects();
    initRippleEffect();
    initImageLoading();
    initAccessibility();
  }

  /**
   * Intersection Observer for scroll-triggered animations
   */
  function initScrollAnimations() {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      console.warn('IntersectionObserver not supported');
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animationPlayState = 'running';
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe all animated elements
    const animatedElements = document.querySelectorAll('.value-card, .values-header, .values-image-wrapper, .values-cta');
    animatedElements.forEach(element => {
      if (element) {
        element.style.animationPlayState = 'paused';
        observer.observe(element);
      }
    });
  }

  /**
   * Parallax scrolling effect
   */
  function initParallaxEffect() {
    let ticking = false;
    
    function updateParallax() {
      const scrolled = window.pageYOffset;
      const sections = document.querySelectorAll('.values-section');
      
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        const speed = 0.5;
        
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          const yPos = -(scrolled * speed);
          section.style.backgroundPositionY = `${yPos}px`;
          
          // Parallax for pseudo elements
          const beforeElement = window.getComputedStyle(section, '::before');
          const afterElement = window.getComputedStyle(section, '::after');
          
          if (beforeElement.content !== 'none') {
            section.style.setProperty('--before-y', `${yPos * 0.3}px`);
          }
          if (afterElement.content !== 'none') {
            section.style.setProperty('--after-y', `${yPos * -0.3}px`);
          }
        }
      });
      
      ticking = false;
    }

    function requestTick() {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }

    // Only enable parallax on desktop
    if (window.innerWidth > 768) {
      window.addEventListener('scroll', requestTick);
    }
  }

  /**
   * Enhanced hover effects with mouse tracking
   */
  function initHoverEffects() {
    const cards = document.querySelectorAll('.value-card');
    
    if (!cards.length) return;
    
    cards.forEach(card => {
      if (!card) return;
      
      // Mouse position tracking
      card.addEventListener('mouseenter', handleMouseEnter);
      card.addEventListener('mousemove', handleMouseMove);
      card.addEventListener('mouseleave', handleMouseLeave);
      
      // 3D tilt effect (desktop only)
      if (window.innerWidth > 768 && !('ontouchstart' in window)) {
        card.addEventListener('mousemove', handle3DTilt);
        card.addEventListener('mouseleave', reset3DTilt);
      }
    });
    
    function handleMouseEnter(e) {
      updateMousePosition(e);
    }
    
    function handleMouseMove(e) {
      updateMousePosition(e);
    }
    
    function handleMouseLeave(e) {
      const card = e.currentTarget;
      card.style.removeProperty('--mouse-x');
      card.style.removeProperty('--mouse-y');
    }
    
    function updateMousePosition(e) {
      const card = e.currentTarget;
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      card.style.setProperty('--mouse-x', `${x}px`);
      card.style.setProperty('--mouse-y', `${y}px`);
    }
    
    function handle3DTilt(e) {
      const card = e.currentTarget;
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = (y - centerY) / 20;
      const rotateY = (centerX - x) / 20;
      
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(10px)`;
    }
    
    function reset3DTilt(e) {
      const card = e.currentTarget;
      card.style.transform = '';
    }
  }

  /**
   * Ripple effect on click
   */
  function initRippleEffect() {
    const clickableElements = document.querySelectorAll('.value-card, .cta-button');
    
    clickableElements.forEach(element => {
      element.addEventListener('click', createRipple);
    });
    
    function createRipple(e) {
      const element = e.currentTarget;
      const ripple = document.createElement('div');
      ripple.classList.add('ripple');
      
      const rect = element.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;
      
      ripple.style.width = ripple.style.height = `${size}px`;
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;
      
      element.appendChild(ripple);
      
      // Remove ripple after animation
      setTimeout(() => {
        ripple.remove();
      }, 600);
    }
  }

  /**
   * Smooth image loading with fade-in
   */
  function initImageLoading() {
    const images = document.querySelectorAll('.values-image');
    
    images.forEach(img => {
      if (!img) return;
      
      // Handle already loaded images
      if (img.complete && img.naturalHeight !== 0) {
        img.classList.add('loaded');
      } else {
        // Add load event listener
        img.addEventListener('load', function() {
          this.classList.add('loaded');
        }, { once: true });
        
        // Error handling
        img.addEventListener('error', function() {
          console.error('Image failed to load:', this.src);
          this.classList.add('error');
          // Optionally set a fallback image
          if (this.dataset.fallback) {
            this.src = this.dataset.fallback;
          }
        }, { once: true });
      }
    });
  }

  /**
   * Accessibility enhancements
   */
  function initAccessibility() {
    // Add keyboard navigation for cards
    const cards = document.querySelectorAll('.value-card');
    
    cards.forEach((card, index) => {
      // Make cards focusable
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'article');
      card.setAttribute('aria-label', `Value ${index + 1}`);
      
      // Keyboard interaction
      card.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const link = this.querySelector('.value-link');
          if (link) {
            link.click();
          }
        }
      });
      
      // Focus styles
      card.addEventListener('focus', function() {
        this.style.outline = '2px solid #0d86c9';
        this.style.outlineOffset = '4px';
      });
      
      card.addEventListener('blur', function() {
        this.style.outline = '';
        this.style.outlineOffset = '';
      });
    });
    
    // Respect prefers-reduced-motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      disableAnimations();
    }
  }

  /**
   * Disable animations for users who prefer reduced motion
   */
  function disableAnimations() {
    const style = document.createElement('style');
    style.textContent = `
      .values-section *, 
      .values-section *::before, 
      .values-section *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Performance optimization: Debounce resize events
   */
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Handle window resize
  const handleResize = debounce(() => {
    // Reinitialize hover effects on resize
    if (window.innerWidth > 768 && !('ontouchstart' in window)) {
      initHoverEffects();
    }
    
    // Dispatch custom event
    document.dispatchEvent(new Event('values-section-resize'));
  }, 250);

  window.addEventListener('resize', handleResize);

  /**
   * Utility function to check if element is in viewport
   */
  function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  /**
   * Performance monitoring (optional)
   */
  if (window.performance && window.performance.mark) {
    window.performance.mark('values-section-init-start');
    
    window.addEventListener('load', () => {
      window.performance.mark('values-section-init-end');
      window.performance.measure(
        'values-section-init',
        'values-section-init-start',
        'values-section-init-end'
      );
      
      const measure = window.performance.getEntriesByName('values-section-init')[0];
      console.log(`Values section initialized in ${measure.duration.toFixed(2)}ms`);
    });
  }

  /**
   * Export functions for external use
   */
  window.ValuesSection = {
    init: initValuesSection,
    refresh: function() {
      initScrollAnimations();
      initImageLoading();
    },
    destroy: function() {
      // Clean up event listeners if needed
      window.removeEventListener('scroll', requestTick);
      window.removeEventListener('resize', handleResize);
    }
  };
})();